import { TextStyleRenderer } from '../renderer/TextStyleRenderer';
import { TextAlignment } from '../../../../enums';
import { isNewline } from '../textMetricsHelpers';
import type { WordMetrics } from './WordMetrics';
import { TextLineMetrics } from './TextLineMetrics';
import {
    assignGloballyUniqueIndex,
    TextMetrics,
    positionLinesBelowEachOther,
    setAlignmentAndTextDirection,
} from './TextMetrics';

/**
 * The maximum percentage of the target area that the last chunk can occupy when taking words from the penultimate chunk.
 * This is used to ensure that the last chunk doesn't take to many words of the penultimate chunk.
 */
const MAX_OCCUPIED_SPACE = 50;

type CanvasLayoutWordMetricsSettings = {
    wordMetricsList?: WordMetrics[];
    style?: TextStyleRenderer;
    alignment?: TextAlignment;
    wordBreak?: boolean;
    maxWidth?: number;
    maxHeight?: number;
    minOccupiedAreaPercentage?: number;
    maxLineCount?: number;
    moveWordsAfterLastPunctuationToNextChunk?: boolean;
    removeTrailingPunctuationOnLastLineFromLastChunk?: boolean;
};

/**
 * Array the words into chunks by following a set of rules. These rules include word wrapping, max line count,
 * move words after last punctuation to next chunk, and remove trailing punctuation on last line from last chunk.
 */
export class LayoutWordMetrics {
    #wordMetricsList: WordMetrics[];
    #style: TextStyleRenderer = new TextStyleRenderer();
    #alignment = TextAlignment.LEFT;
    #wordBreak = true;
    #maxWidth = 400;
    #maxHeight = 400;

    // rules
    #minOccupiedAreaPercentage = 20;
    #maxLineCount = -1;
    #moveWordsAfterLastPunctuationToNextChunk = false;
    #removeTrailingPunctuationOnLastLineFromLastChunk = false;

    chunks: TextMetrics[] = [];

    #lines: TextLineMetrics[] = [];
    #baseLine = 0;

    private constructor(settings: CanvasLayoutWordMetricsSettings) {
        this.#setSettings(settings);
    }

    get #baselineOffset() {
        return this.#style.baselineOffset;
    }

    #generateChunks() {
        this.#wordMetricsList.forEach((word) => word.clearCachedValues());

        if (this.#baselineOffset > this.#maxHeight) {
            console.log(
                `text it too tall to fit in the bounds: ${this.#baselineOffset} > ${this.#maxHeight}`,
            );
            return;
        }

        this.#baseLine = 0;
        this.#createNewLine();
        this.#wordMetricsList.forEach((wordMetrics) => {
            if (isNewline(wordMetrics.text)) {
                this.#currentLine.add(wordMetrics);
                this.#createNewLine();
            } else {
                const futureLineWidth = wordMetrics.advanceWidth + this.#currentLine.advanceWidth;
                if (this.#wordBreak && futureLineWidth > this.#maxWidth) {
                    this.#createNewLine();
                }
                this.#currentLine.add(wordMetrics);
            }
        });
        if (this.#lines.length > 0) {
            this.#createNewChunk();
        }
    }

    #run() {
        this.#generateChunks();

        if (this.#removeTrailingPunctuationOnLastLineFromLastChunk && this.chunks.length !== 0) {
            this.chunks.at(-1).removeTrailingPunctuations();
        }
    }

    get #currentLine() {
        return this.#lines.at(-1);
    }

    #createNewLine() {
        let wordsAfterPunctuation: WordMetrics[] = [];

        const useMaxLineRule = this.#maxLineCount > 0 && this.#lines.length >= this.#maxLineCount;
        const nextLineIsInNextChunk = this.#baseLine + this.#baselineOffset > this.#maxHeight;
        if (nextLineIsInNextChunk || useMaxLineRule) {
            if (
                this.#moveWordsAfterLastPunctuationToNextChunk &&
                !!this.#currentLine &&
                this.#currentLine.hasInLinePunctuations()
            ) {
                wordsAfterPunctuation = this.#currentLine.removeWordsAfterLastInLinePunctuation();
            }
            this.#createNewChunk();
        }

        const line = new TextLineMetrics();
        this.#lines.push(line);

        wordsAfterPunctuation.forEach((word) => line.add(word));

        this.#baseLine += this.#baselineOffset;
        return line;
    }

    #createNewChunk() {
        this.#lines.forEach((line) => line.removeLeadingTrailingSpaces(this.#alignment));

        const lineWordMetrics = this.#lines.flatMap((line) => line.words);
        assignGloballyUniqueIndex(lineWordMetrics);

        positionLinesBelowEachOther(this.#lines, this.#style);
        setAlignmentAndTextDirection(this.#lines, this.#alignment);

        const textMetrics = new TextMetrics(this.#lines);
        this.chunks.push(textMetrics);
        this.#lines = [];
        this.#baseLine = 0;
    }

    get #maxArea(): number {
        return this.#maxWidth * this.#maxHeight;
    }

    #getSettings(): CanvasLayoutWordMetricsSettings {
        return {
            wordMetricsList: this.#wordMetricsList,
            style: this.#style,
            alignment: this.#alignment,
            wordBreak: this.#wordBreak,
            maxWidth: this.#maxWidth,
            maxHeight: this.#maxHeight,
            minOccupiedAreaPercentage: this.#minOccupiedAreaPercentage,
            maxLineCount: this.#maxLineCount,
            moveWordsAfterLastPunctuationToNextChunk:
                this.#moveWordsAfterLastPunctuationToNextChunk,
            removeTrailingPunctuationOnLastLineFromLastChunk:
                this.#removeTrailingPunctuationOnLastLineFromLastChunk,
        };
    }

    #setSettings(settings: CanvasLayoutWordMetricsSettings) {
        this.#wordMetricsList = settings.wordMetricsList ?? this.#wordMetricsList;
        this.#style = settings.style ?? this.#style;
        this.#alignment = settings.alignment ?? this.#alignment;
        this.#wordBreak = settings.wordBreak ?? this.#wordBreak;
        this.#maxWidth = settings.maxWidth ?? this.#maxWidth;
        this.#maxHeight = settings.maxHeight ?? this.#maxHeight;
        this.#minOccupiedAreaPercentage =
            settings.minOccupiedAreaPercentage ?? this.#minOccupiedAreaPercentage;
        this.#maxLineCount = settings.maxLineCount ?? this.#maxLineCount;
        this.#moveWordsAfterLastPunctuationToNextChunk =
            settings.moveWordsAfterLastPunctuationToNextChunk ??
            this.#moveWordsAfterLastPunctuationToNextChunk;
        this.#removeTrailingPunctuationOnLastLineFromLastChunk =
            settings.removeTrailingPunctuationOnLastLineFromLastChunk ??
            this.#removeTrailingPunctuationOnLastLineFromLastChunk;
    }

    /**
     * Lays out the given word metrics into TextMetrics "chunks" based on the given width, height, style and
     * alignment while respecting our layout rules.
     */
    static layout({
        alignment = TextAlignment.LEFT,
        wordMetricsList = [],
        style,
        wordBreak = true,
        maxWidth = 400,
        maxHeight = 400,
        minOccupiedAreaPercentage = 20,
        maxLineCount = -1,
        moveWordsAfterLastPunctuationToNextChunk = false,
        removeTrailingPunctuationOnLastLineFromLastChunk = false,
    }: {
        alignment?: TextAlignment;
        wordMetricsList?: WordMetrics[];
        style: TextStyleRenderer;
        wordBreak?: boolean;
        maxWidth?: number;
        maxHeight?: number;
        /** Forces a minimum occupied space for the last chunk. This parameter takes into affect when its value is greater than 0. It's default value is 20. */
        minOccupiedAreaPercentage?: number;
        /** Forces a maximum number of lines per chunk. This parameter takes into affect when its value is greater than 0. It's default value is -1. */
        maxLineCount?: number;
        moveWordsAfterLastPunctuationToNextChunk?: boolean;
        removeTrailingPunctuationOnLastLineFromLastChunk?: boolean;
    }) {
        if (minOccupiedAreaPercentage > MAX_OCCUPIED_SPACE) {
            throw new Error('minOccupiedAreaPercentage must be less than 50%');
        }
        const layout = new LayoutWordMetrics({
            alignment: alignment,
            wordMetricsList: wordMetricsList,
            style: style,
            wordBreak: wordBreak,
            maxWidth: maxWidth,
            maxHeight: maxHeight,
            minOccupiedAreaPercentage: minOccupiedAreaPercentage,
            maxLineCount: maxLineCount,
            moveWordsAfterLastPunctuationToNextChunk: moveWordsAfterLastPunctuationToNextChunk,
            removeTrailingPunctuationOnLastLineFromLastChunk:
                removeTrailingPunctuationOnLastLineFromLastChunk,
        });
        layout.#run();

        LayoutWordMetrics.#moveWordsUntilMinOccupiedAreaPercentageIsReached(layout);

        return layout.chunks;
    }

    static #moveWordsUntilMinOccupiedAreaPercentageIsReached(layout: LayoutWordMetrics) {
        const minOccupiedAreaPercentage = layout.#minOccupiedAreaPercentage;
        if (layout.#maxLineCount > 0 && layout.#maxLineCount < 3) {
            return;
        }
        if (minOccupiedAreaPercentage <= 0 || layout.chunks.length < 2) {
            return;
        }

        //  calculate the occupied area of the last chunk
        const targetArea = layout.#maxArea;
        const lastMetrics = layout.chunks.at(-1);
        const occupiedAreaPercentage = occupiedAreaInPercentage(lastMetrics.area, targetArea);
        if (occupiedAreaPercentage >= minOccupiedAreaPercentage) {
            return;
        }

        const settings = layout.#getSettings();
        const penultimateMetrics = layout.chunks.at(-2);

        // move words from the penultimate metrics to the last metrics until we reach the minOccupiedSpace
        const words = penultimateMetrics.wordMetrics;
        const wordsToMove = moveWordsUntilTargetAreaPercentageIsReached(
            words,
            occupiedAreaPercentage,
            minOccupiedAreaPercentage,
            targetArea,
        );

        layout.chunks.pop();
        layout.chunks.pop();

        function layoutWordMetrics(
            wordMetricsList: WordMetrics[],
            settings: CanvasLayoutWordMetricsSettings,
        ) {
            const layout = new LayoutWordMetrics({
                ...settings,
                wordMetricsList,
            });
            layout.#wordMetricsList.forEach((word) => {
                word.elements.forEach((element) => element.show());
                word.clearCachedValues();
            });
            layout.#run();
            return layout.chunks[0];
        }

        const penultimateWords = words.filter((word) => !wordsToMove.includes(word));
        layout.chunks.push(layoutWordMetrics(penultimateWords, settings));

        const lastWords = wordsToMove.reverse().concat(lastMetrics.wordMetrics);
        layout.chunks.push(layoutWordMetrics(lastWords, settings));
    }
}

function moveWordsUntilTargetAreaPercentageIsReached(
    words: WordMetrics[],
    startAreaPercentage: number,
    targetAreaPercentage: number,
    targetArea: number,
) {
    let wordCount = words.length - 1;
    const wordsToMove = [];
    while (startAreaPercentage < targetAreaPercentage) {
        if (wordCount < 0) {
            throw new Error('We have run out of words to move');
        }
        const word = words[wordCount];
        const wordArea = word.width * (word.height * word.style.lineHeight);
        startAreaPercentage += occupiedAreaInPercentage(wordArea, targetArea);
        wordsToMove.push(word);
        wordCount--;
    }
    return wordsToMove;
}

function occupiedAreaInPercentage(sourceArea: number, targetArea: number) {
    return Math.round((sourceArea / targetArea) * 100);
}
